import { ParsedUrlQuery } from 'querystring';

import { loadGraphParser } from '@squareup/dex-data-access-oas';
import { getDeveloperApi } from '@squareup/dex-data-shared-developer-api';
import {
  setGlobalError,
  setPageError,
} from '@squareup/dex-data-shared-main-data-slices';
import {
  setGraphEntitiesInView,
  setParsedGraphInView,
  setGraphEntitiesInViewLoading,
  setSpecVersionInView as setSpecVersion,
} from '@squareup/dex-tech-data-slices';
import {
  TechRefStoreAsyncAction,
  TechRefStoreDispatch,
  TechRefStoreState,
} from '@squareup/dex-tech-shell';
import {
  selectSpecVersionInView,
  selectGraphEntitiesInViewError,
  selectParsedGraphInfoError,
} from '@squareup/dex-tech-store-access-selectors';
import { StateNodeError, StoreStateNode } from '@squareup/dex-types-data-state';
import {
  stateErrorToCustomError,
  publishActionableError,
} from '@squareup/dex-utils-error';
import { INTERNAL_SERVER_ERROR_CODE } from '@squareup/dex-utils-shared-http-status';

/**
 * Gets the parsed spec version state given a url query.
 */
async function getParsedGraphVersionFromUrl(
  dispatch: TechRefStoreDispatch,
  getState: () => TechRefStoreState,
  query: ParsedUrlQuery
): Promise<
  ReturnType<typeof selectSpecVersionInView>['parsedInfo'] | undefined
> {
  // Set the spec version
  await dispatch(setSpecVersion(query));

  // Validate spec version
  const {
    error: versionError,
    versionInfo,
    parsedInfo,
  } = selectSpecVersionInView(getState());

  if (versionError || !versionInfo) {
    return undefined;
  }

  const currentParsedVersion = parsedInfo?.version;

  // If the last parsed version was the same noop
  if (currentParsedVersion && currentParsedVersion === versionInfo.version) {
    return { version: currentParsedVersion };
  }

  // Kick off a request to pull in our parser while we fetch the data.
  const parserPromise = loadGraphParser();

  // Fetch the spec
  await dispatch(
    getDeveloperApi().endpoints.getSpecification.initiate(versionInfo)
  );

  // Validate spec
  const { data, error } = getDeveloperApi().endpoints.getSpecification.select(
    versionInfo
  )(getState());

  let storeStateError: StoreStateNode<unknown>['error'] = undefined;
  let version: string | undefined = undefined;

  if (error) {
    // There was some kind of error in an upstream service.
    // We need to convert the error to a custom store node
    // error type to set on the parsed view state.
    storeStateError = stateErrorToCustomError(
      error,
      'Error fetching spec content.',
      `Version: ${versionInfo.version}`
    );
  } else if (!data) {
    storeStateError = {
      data: 'data undefined',
      error: 'No content return in from api.',
      originalStatus: INTERNAL_SERVER_ERROR_CODE,
      status: 'PARSING_ERROR',
    };
  } else {
    // We use the document ID or the version from the content as our parsed version.
    version =
      'version' in data.content
        ? data.content.version
        : data.content.document_id;

    if (!version) {
      storeStateError = {
        data: `Version: ${version}`,
        error: 'No spec version found in content.',
        originalStatus: INTERNAL_SERVER_ERROR_CODE,
        status: 'PARSING_ERROR',
      };
    }
  }

  if (storeStateError) {
    publishActionableError(storeStateError);
    await dispatch(setParsedGraphInView({ error: storeStateError }));

    return undefined;
  }

  // Parse the graph
  // Parse the spec into a graph for later use. Graphs cannot be kept in state as its not serializable, thus just keep the parsed version info.
  const { parse } = await parserPromise;
  const graph = data && parse(data);

  if (!graph) {
    const error: StateNodeError = {
      data: `Version: ${version}`,
      error: 'Unable to parse graph via ignition.',
      originalStatus: 500,
      status: 'PARSING_ERROR',
    };

    publishActionableError(error);

    // Set parsed errror state
    await dispatch(setParsedGraphInView({ error }));

    return undefined;
  }

  // Set the parsed version
  // Make sure to use the content version from the devs-content response, and
  // not the version of the OAS graph. All checks rely on the devs-content version
  // from here. In the future, it would be good to rely on the OAS version, as they
  // should be the same generally.
  await dispatch(setParsedGraphInView({ version }));

  return { version };
}

/**
 * Loads the appropriate graph entities into view given the parsed query.
 */
const loadGraphEntitiesIntoView: (
  _query: ParsedUrlQuery,
  _apiExplorerQuery?: boolean
) => TechRefStoreAsyncAction<void> =
  (query, apiExplorerQuery = false) =>
  async (dispatch, getState) => {
    await dispatch(setGraphEntitiesInViewLoading({ isLoading: true }));

    const graphVersion = await getParsedGraphVersionFromUrl(
      dispatch,
      getState,
      query
    );

    if (graphVersion?.version) {
      // Set the entities
      await dispatch(
        setGraphEntitiesInView({
          query,
          version: graphVersion.version,
          apiExplorerQuery,
        })
      );
    }

    const parseError = selectParsedGraphInfoError(getState());
    const pageError = selectGraphEntitiesInViewError(getState());

    if (parseError) {
      await dispatch(setGlobalError(parseError));
    } else if (pageError) {
      // All the graph entities in view will make a check
      // for the appropriate entities to be available. If it errors,
      // we end up here.
      await dispatch(setPageError(pageError));
    }

    await dispatch(setGraphEntitiesInViewLoading({ isLoading: false }));
  };

export { loadGraphEntitiesIntoView };
